import { ROLE_CAPABILITIES } from "../config/roleCapabilities";

const POSITION_MAP = {
  nurse: "Nurse",
  medical_assistant: "Medical Assistant",
  physician: "Physician",
  medical_biller: "Medical Biller",
  care_manager: "Care Manager",
};

const ROLE_SLUG_PREFIXES = {
  TOCA: "toca_",
  NON_TOCA: "non_toca_",
};

const SERVICE_TYPE_INDICATORS = {
  BASIC: "_basic_",
  FULL: "_full_",
};

export function getUserFromStorage() {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.warn("[authorization] Failed to parse user from localStorage:", e);
    return null;
  }
}

export function getUserRoleSlug(user = null) {
  if (!user) user = getUserFromStorage();
  const slug = user?.role_slug;
  return typeof slug === "string" && slug.length > 0 ? slug : null;
}

export function canAccessFeature(user, featurePath) {
  if (!user || !featurePath) return false;

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return false;

  const roleCapabilities = ROLE_CAPABILITIES[roleSlug];
  if (!roleCapabilities) return false;

  const parts = featurePath.split(".");
  let result = roleCapabilities;

  for (const part of parts) {
    if (result && typeof result === "object" && part in result) {
      result = result[part];
    } else {
      return false;
    }
  }

  return typeof result === "boolean" ? result : false;
}

export function canAccess(user, page) {
  if (!user) return false;

  if (page === "my_tasks") {
    return canAccessMyTasks(user);
  }

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return false;

  return ROLE_CAPABILITIES[roleSlug]?.pages?.[page] ?? false;
}

export function canAccessMyTasks(user) {
  return user?.my_tasks_access ?? false;
}

export function canAccessNurseTasks(user) {
  return canAccessFeature(user, "my_tasks.nurse_tasks");
}

export function getCopilotAccess(user) {
  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return { enabled: false, editable: false };

  const config = ROLE_CAPABILITIES[roleSlug]?.copilot;
  return config || { enabled: false, editable: false };
}

export function getMyTasksAccess(user) {
  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return { enabled: false, editable: false, nurse_tasks: false };

  const config = ROLE_CAPABILITIES[roleSlug]?.my_tasks;
  return config || { enabled: false, editable: false, nurse_tasks: false };
}

export function getUserEnv(user = null) {
  if (!user) user = getUserFromStorage();
  if (!user) return null;

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return null;

  return roleSlug.startsWith(ROLE_SLUG_PREFIXES.TOCA) ? "TOCA" : "NON_TOCA";
}

// export function getUserServiceType(user = null) {
//   if (!user) user = getUserFromStorage();
//   if (!user) return null;

//   const roleSlug = getUserRoleSlug(user);
//   if (!roleSlug || roleSlug.startsWith(ROLE_SLUG_PREFIXES.TOCA)) {
//     return null;
//   }

//   if (roleSlug.includes(SERVICE_TYPE_INDICATORS.BASIC)) return "Basic";
//   if (roleSlug.includes(SERVICE_TYPE_INDICATORS.FULL)) return "Full";
//   return null;
// }

export function getUserPosition(user = null) {
  if (!user) user = getUserFromStorage();
  if (!user) return null;

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return null;

  const userPosition = roleSlug.split("_").pop();
  return POSITION_MAP[userPosition] || null;
}

export function getUserIdentity(user = null) {
  if (!user) user = getUserFromStorage();
  if (!user) return null;
  const staff_id = user?.staff_id || null;
  const slug = getUserRoleSlug(user);
  if (!slug) return null;

  const isToca = slug.startsWith(ROLE_SLUG_PREFIXES.TOCA);
  const isNonToca = slug.startsWith(ROLE_SLUG_PREFIXES.NON_TOCA);

  return {
    slug,
    isToca,
    isNonToca,
    isAdmin: slug.includes("admin"),
    isSuperAdmin: slug.includes("super_admin") || slug.includes("super_user"),
    isNurse: slug.includes("nurse"),
    isCareManager: slug.includes("care_manager") || slug === "toca_nurse_admin",
    isPhysician: slug.includes("physician"),
    isMedicalBiller: slug.includes("medical_biller"),
    isMedicalAsst: slug.includes("medical_assistant"),
    isPatient: slug.includes("patient"),
    isBasic: isNonToca && slug.includes(SERVICE_TYPE_INDICATORS.BASIC),
    isFull: isNonToca && slug.includes(SERVICE_TYPE_INDICATORS.FULL),
    hasCrossAccountAccess: canAccessFeature(user, "cross_account_access"),
    hasstaffPageTasksAccess: canAccessFeature(user, "SatffPage.tasks"),
    hasstaffPageReportsAccess: canAccessFeature(user, "SatffPage.reports"),
    hasstaffPageEscalationLogAccess: canAccessFeature(user, "SatffPage.escalation_log"),
    hasMyTasksAccess: user?.my_tasks_access || canAccessMyTasks(user) ? true : false,
    hasChatbotAccess: user?.chatbot_access || canAccessFeature(user, "copilot") ? true : false,
    staff_id,
  };
}

// export function isAdminUser(user) {
//   return canAccessFeature(user, "admin");
// }

export function hasCrossAccountAccess(user) {
  return canAccessFeature(user, "cross_account_access");
}

export function canAccessOrganization(user, tenantId = null, locationId = null) {
  if (!user) return false;

  if (hasCrossAccountAccess(user)) {
    return true;
  }

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return false;

  if (roleSlug.startsWith(ROLE_SLUG_PREFIXES.TOCA)) {
    return true;
  }

  if (roleSlug.startsWith(ROLE_SLUG_PREFIXES.NON_TOCA)) {

    if (!tenantId) return true;

    const tenantAccess = user.accessible_tenants?.find(t => t.tenant_id === tenantId);
    if (!tenantAccess) return false;

    if (locationId) {
      return tenantAccess.location_ids?.includes(locationId) ?? false;
    }
  }

  return true;
}

export function getAllowedPages(user) {
  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return {};

  return ROLE_CAPABILITIES[roleSlug]?.pages || {};
}

